import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getStory, getStoryBySlug, submitFeedback, reactStory } from '../../api/storyApi';
import FeedbackSummaryDialog from './FeedbackSummaryDialog';
import './PlayStory.css';
import Modal from '../common/Modal';
import StoryEndDialog from './StoryEndDialog';
import { Helmet } from 'react-helmet';
import renderNarrative from '../../utils/renderNarrative';
import LoadingOverlay from '../common/LoadingOverlay';


const PlayStory = () => {
  // const { id } = useParams();
  const { slug } = useParams();
  const [story, setStory] = useState(null);
  const [userFeedback, setUserFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [showStoryEndDialog, setShowStoryEndDialog] = useState(false);
    
  const audioRef = React.useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();

  const isValidObjectId = (str) => /^[a-f\d]{24}$/i.test(str);

  useEffect(() => {
    const fetchStory = async () => {
      try {
        if (isValidObjectId(slug)) {
          const realStoryData = await getStory(slug);
          if (realStoryData && realStoryData.story) {
            return navigate(`/story/${realStoryData.story.slug}`, { replace: true });
          }
        }

        const response = await getStoryBySlug(slug);
        setStory(response.story);
        setUserFeedback(response.userFeedback);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching story', error);
        setLoading(false);
      }
    };
    fetchStory();
  }, [slug, navigate]);

  useEffect(() => {
    const audio = audioRef?.current;

    const handleCanPlayThrough = () => {
      setTimeout(() => {
        if (audio) audio.play();
        setIsPlaying(true);
      }, 1000);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
    }

    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setAudioLoaded(false);
      audio.load();
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
    }

    return () => {
      if (audio) {
        audio.pause();
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);  
      }
    };    
  }, [currentPartIndex]);

  useEffect(() => {
    const play = () => {
      setTimeout(() => {
        if (audioRef.current) audioRef.current.play();
        setIsPlaying(true);
      }, 1000);
    }

    if (audioLoaded && audioRef.current) {
      play();
    }
  }, [audioLoaded]);

  const parts = ['opening', 'buildUp', 'dilemma', 'resolution', 'closing'];

  const currentPart = parts[currentPartIndex];
  const currentImage = story?.images?.[currentPart];
  const currentAudio = story?.audio?.[currentPart];
  const currentText = story?.narrative?.[currentPart];

  const handleImageClick = () => setShowFullscreenImage(true);
  const handleCloseImage = () => setShowFullscreenImage(false);

  // Play audio
  const playAudio = () => {
    if (audioLoaded && audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
    }
  };

  // Pause audio
  const pauseAudio = () => {
      if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
      }
  };

  // Restart audio
  const restartAudio = () => {
      if (audioRef.current) {
          audioRef.current.currentTime = 0;
          playAudio();
      }
  };

  // Jump to previous part
  const previousPart = () => {
      if (currentPartIndex > 0) {
        setCurrentPartIndex((prev) => prev - 1);
      }
  };

  // Jump to next part
  const nextPart = () => {
      if (currentPartIndex < parts.length - 1) {
        setCurrentPartIndex((prev) => prev + 1);
      }
  };

  const restartStory = () => {
    setCurrentPartIndex(0);
  }

  const handleAudioEnd = () => {
    if (currentPartIndex < parts.length - 1) {
        nextPart();
    } else {
      setIsPlaying(false); // Stop playback if it's the last part
      setShowFullscreenImage(false);
      if (
        user &&
        story.user &&
        String(user._id || user.id) !== String(story.user) &&
        !userFeedback
      ) {
        setShowSummaryDialog(true);
      } else {
        setShowStoryEndDialog(true);
      }
    }
  };

  const handleAudioLoaded = () => {
    setAudioLoaded(true); // Mark audio as fully loaded
  };

  const handleSubmitFeedback = async ({ reaction, feedback }) => {
    try {
      if (story && story._id) {
        await reactStory(story._id, reaction);
        await submitFeedback(story._id, feedback);
      }
    } catch (err) {
      console.error('Failed to send feedback:', err);
    }
  };

  if (loading) {
    return ( <LoadingOverlay message={t('playStory.loadingStory')} /> );
  }

  if (!story) {
    return (
      <Modal isOpen={true} onClose={() => navigate('/')}>
        <h2>{t('playStory.noStoryFound')}</h2>
        <p>{t('playStory.noStoryFoundText')}</p>
        <div className="modal-buttons">
            <button className="action-btn" onClick={() => navigate('/')}>{t('playStory.ok')}</button>
        </div>
      </Modal>
    );
  }

  if (!story.narrative || !story.images || !story.audio) {
    return (
      <Modal isOpen={true} onClose={() => navigate('/')}>
        <h2>{t('playStory.storyUnreadable')}</h2>
        <p>{t('playStory.storyUnreadableText')}</p>
        <div className="modal-buttons">
            <button className="action-btn" onClick={() => navigate('/')}>{t('playStory.ok')}</button>
        </div>
      </Modal>
    );
  }

  const title = story?.title || 'StoryBuddie';
  const description = story?.description || t('meta.genericDescription');
  const image = story?.coverImage || '/images/default-cover.png';
  const url = `${window.process.env.REACT_FRONTEND_URL}/story/${story?.slug}`;

  return (
    <>
    <Helmet>
        <title>{title}</title>
        <link
          rel="canonical"
          href={`${window.process.env.REACT_FRONTEND_URL}/story/${story.slug}`}
        />
        <meta name="description" content={description} />

        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
    </Helmet>
    <div className="playing-story">

      {/* Navbar */}
      {!showSummaryDialog && !showStoryEndDialog && 
      <nav
        className="play-story-navbar"
        // onMouseOver={(e) => e.currentTarget.classList.add('opaque')}
        // onMouseOut={(e) => e.currentTarget.classList.remove('opaque')}
      >
        <button className='play-story-nav-button' onClick={() => navigate('/#my-stories')}>
            <div className="play-story-nav-button-icon">
              <i className="fas fa-list"></i>
            </div>
            {/* <span className="play-story-nav-button-label">My Stories</span> */}
        </button>
        <button className='play-story-nav-button' onClick={() => setCurrentPartIndex(0)}>
            <div className="play-story-nav-button-icon">
              <i className="fas fa-backward-fast"></i>
            </div>
            {/* <span className="play-story-nav-button-label">My Stories</span> */}
        </button>
        <button className='play-story-nav-button' onClick={previousPart} disabled={currentPartIndex === 0}>
            <div className="play-story-nav-button-icon">
              <i className="fas fa-backward"></i>
            </div>
            {/* <span className="play-story-nav-button-label">Previous</span> */}
        </button>
        {isPlaying ? (
          <button className='play-story-nav-button' onClick={pauseAudio}>
            <div className="play-story-nav-button-icon">
              <i className="fas fa-pause"></i>
            </div>
            {/* <span className="play-story-nav-button-label">Pause</span> */}
          </button>
        ) : (
          <button className='play-story-nav-button' onClick={playAudio} disabled={!audioLoaded}>
            <div className="play-story-nav-button-icon">
              <i className="fas fa-play"></i>
            </div>
            {/* <span className="play-story-nav-button-label">Play</span> */}
          </button>
        )}
        <button className='play-story-nav-button' onClick={restartAudio} disabled={!audioLoaded}>
          <div className="play-story-nav-button-icon">
            <i className="fas fa-rotate-left"></i>
          </div>
          {/* <span className="play-story-nav-button-label">Restart</span> */}
        </button>
        <button className='play-story-nav-button' onClick={nextPart} disabled={currentPartIndex === parts.length - 1}>
          <div className="play-story-nav-button-icon">
            <i className="fas fa-forward"></i>
          </div>
          {/* <span className="play-story-nav-button-label">Next</span> */}
        </button>
      </nav>
    }
      

      <div className='story-content'>
        {/* Illustration */}
        <div className='play-story-illustration'>
            {currentImage && (
                <img className='play-story-image'
                    src={currentImage}
                    alt={`Illustration for ${currentPart}`}
                    onClick={handleImageClick}
                />
            )}
        </div>
      
        {/* Story Text */}
        <div className='play-story-text' >
          <h3 className='play-story-title'>{story?.title}</h3>
          {renderNarrative(currentText, t)}
        </div>
        
      </div>

      {showFullscreenImage && (
        <div className="fullscreen-image-overlay" onClick={handleCloseImage}>
          <img className="fullscreen-image" src={currentImage} alt="Fullscreen" />
        </div>
      )}

      <FeedbackSummaryDialog
        isOpen={showSummaryDialog}
        storyTitle={story?.title}
        storyCoverImage={story?.coverImage}
        onSubmit={handleSubmitFeedback}
        onClose={() => setShowSummaryDialog(false)}
      />

      <StoryEndDialog
        isOpen={showStoryEndDialog}
        storyTitle={story?.title}
        storyCoverImage={story?.coverImage}
        onReplay={restartStory}
        onClose={() => setShowStoryEndDialog(false)}
      />

      {currentAudio && (<audio
        ref={audioRef}
        src={currentAudio}
        onLoadedData={handleAudioLoaded}
        onEnded={handleAudioEnd}
      />)}
      
    </div>
    </>

  );
};

export default PlayStory;
